/**
 * Analytics Utility - Dual Tracking
 * ==================================
 * Sends events to both:
 * 1. Our MongoDB backend (real-time, no delays)
 * 2. GA4 (for backup/comparison)
 */

// Use empty string for relative URLs - works in both dev and production
const API_URL = '';

// Debug mode - enable for development testing
const DEBUG_ANALYTICS = true;

/**
 * Track an analytics event
 * @param {string} eventName - Name of the event
 * @param {object} params - Additional parameters
 */
export const trackEvent = async (eventName, params = {}) => {
  // Get session ID from storage or create one
  let sessionId = sessionStorage.getItem('mcm_session_id');
  if (!sessionId) {
    sessionId = 'sess_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
    sessionStorage.setItem('mcm_session_id', sessionId);
  }

  const eventData = {
    event_name: eventName,
    params: params,
    session_id: sessionId,
    page_url: window.location.pathname,
    referrer: document.referrer || null
  };

  if (DEBUG_ANALYTICS) {
    console.log(`📊 [MCM Analytics] Tracking: ${eventName}`, eventData);
  }

  // 1. Send to our backend (primary - real-time)
  try {
    const response = await fetch(`${API_URL}/api/analytics/track`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(eventData),
      // Don't wait for response - fire and forget
      keepalive: true
    });
    
    if (DEBUG_ANALYTICS) {
      if (response.ok) {
        console.log(`✅ [MCM Analytics] Event sent: ${eventName}`);
      } else {
        console.warn(`⚠️ [MCM Analytics] Event failed: ${eventName}`, response.status);
      }
    }
  } catch (e) {
    console.warn('Failed to send to backend:', e);
  }

  // 2. Send to GA4 (backup)
  if (typeof window !== 'undefined' && window.gtag) {
    if (DEBUG_ANALYTICS) console.log(`📊 GA4 Event: ${eventName}`, params);
    window.gtag('event', eventName, { debug_mode: true, ...params });
  }
};

/**
 * Track page view
 * Called automatically on route changes
 */
export const trackPageView = (pagePath) => {
  trackEvent('page_view', { page_path: pagePath || window.location.pathname });
};

/**
 * Pre-defined event trackers for common actions
 */
export const analytics = {
  // Modal events
  modalView: () => trackEvent('modal_view'),
  modalCtaClick: (intent) => trackEvent('modal_cta_click', { intent }),
  modalDismiss: () => trackEvent('modal_dismiss'),
  
  // CTA events
  reviewerCtaClick: () => trackEvent('reviewer_cta_click'),
  artistCtaClick: () => trackEvent('artist_cta_click'),
  bookClick: () => trackEvent('book_click', { destination: 'gumroad' }),
  
  // Signup events
  signupStart: () => trackEvent('reviewer_signup_start'),
  signupComplete: (userIntent) => trackEvent('reviewer_signup_complete', { 
    user_intent: userIntent || sessionStorage.getItem('mcm_user_intent') || 'direct' 
  }),
  
  // Submission events
  submitStart: () => trackEvent('artist_submit_start'),
  submitComplete: (paymentType) => trackEvent('artist_submit_complete', { payment_type: paymentType }),
  
  // Tip events
  tipClick: (tipType) => trackEvent('tip_click', { tip_type: tipType }),
  
  // Payment events
  supportClick: (method) => trackEvent('support_click', { method })
};

export default trackEvent;
